<template>
  <div class="relative">
    <input
      :id="id"
      v-model="localValue"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :class="inputClass"
      @input="handleInput"
      @blur="handleBlur"
      @keypress="handleKeypress"
      :aria-label="ariaLabel"
      :aria-invalid="!isValid"
      autocomplete="off"
    >
    
    <!-- Unit display -->
    <div v-if="unit" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
      <span class="text-gray-500 text-sm">{{ unit }}</span>
    </div>
    
    <!-- Error message -->
    <p v-if="showError && errorMessage" class="mt-1 text-sm text-red-600">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  restrictDecimalInput,
  validateMaxValue,
  replaceDotWithComma,
  parseGermanDecimal,
  formatGermanDecimal
} from '../utils/numberFormat'

interface Props {
  modelValue: string | number | null
  placeholder?: string
  disabled?: boolean
  required?: boolean
  maxValue?: number
  maxDigitsBeforeComma?: number
  maxDigitsAfterComma?: number
  unit?: string
  id?: string
  ariaLabel?: string
  showError?: boolean
  inputClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'z.B. 12,50',
  disabled: false,
  required: false,
  maxValue: 9999.99,
  maxDigitsBeforeComma: 4,
  maxDigitsAfterComma: 2,
  showError: true,
  inputClass: 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#E9041D] focus:outline-none focus:ring-2 focus:ring-[#E9041D] focus:ring-opacity-50'
})

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
  'blur': [value: number | null]
  'input': [value: number | null]
}>()

// Local value for display (with comma)
const localValue = ref('')
const isValid = ref(true)
const errorMessage = ref('')

// Initialize local value from prop
watch(() => props.modelValue, (newValue) => {
  if (newValue === null || newValue === undefined || newValue === '') {
    localValue.value = ''
  } else {
    // Convert to German format for display
    localValue.value = replaceDotWithComma(String(newValue))
  }
}, { immediate: true })

// Handle input event
const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value
  
  // Apply input restrictions
  const restricted = restrictDecimalInput(
    value,
    props.maxDigitsBeforeComma,
    props.maxDigitsAfterComma
  )
  
  // Update local value
  localValue.value = restricted
  
  // Validate max value
  if (restricted) {
    const isValidValue = validateMaxValue(restricted, props.maxValue)
    isValid.value = isValidValue
    
    if (!isValidValue) {
      errorMessage.value = `Maximaler Wert: ${formatGermanDecimal(props.maxValue, props.maxDigitsAfterComma)}`
    } else {
      errorMessage.value = ''
    }
  } else {
    isValid.value = !props.required
    errorMessage.value = props.required ? 'Dieses Feld ist erforderlich' : ''
  }
  
  // Parse and emit numeric value
  const numericValue = parseGermanDecimal(restricted)
  emit('update:modelValue', numericValue)
  emit('input', numericValue)
}

// Handle blur event
const handleBlur = () => {
  // Format value on blur if valid
  if (localValue.value && isValid.value) {
    const numericValue = parseGermanDecimal(localValue.value)
    if (numericValue !== null) {
      localValue.value = formatGermanDecimal(numericValue, props.maxDigitsAfterComma)
    }
  }
  
  const numericValue = parseGermanDecimal(localValue.value)
  emit('blur', numericValue)
}

// Handle keypress to prevent invalid characters
const handleKeypress = (event: KeyboardEvent) => {
  const char = String.fromCharCode(event.which || event.keyCode)
  
  // Allow control keys
  if (event.ctrlKey || event.metaKey) {
    return
  }
  
  // Allow digits
  if (/[0-9]/.test(char)) {
    return
  }
  
  // Allow comma if not already present
  if (char === ',' && !localValue.value.includes(',')) {
    return
  }
  
  // Block everything else
  event.preventDefault()
}
</script>